/**
 * @userstory US-STRIPE-01, US-STRIPE-02, US-STRIPE-03, US-SUB-01, US-SUB-03, US-SUB-04, US-SUB-05
 *
 * API client for subscription management endpoints.
 * Provides methods for viewing subscriptions (US-STRIPE-01 / US-SUB-01),
 * generating Stripe Customer Portal URLs (US-STRIPE-02 / US-SUB-03),
 * and synchronizing subscription state with Stripe (US-STRIPE-03).
 */
import apiClient from './apiClient';

// ============================================================================
// Types
// ============================================================================

/**
 * Response from GET /api/organizations/{id}/subscriptions/portal
 * or GET /api/talents/{id}/subscriptions/portal
 */
export interface PortalResponse {
	url: string;
}

export interface PortalErrorResponse {
	code: string;
	messageKey: string;
}

/**
 * Response from GET /api/organizations/{id}/subscriptions/current
 * or GET /api/talents/{id}/subscriptions/current
 */
export interface CurrentSubscription {
	plan: string; // "Free" | "Plus" | "Pro"
	status: string; // "Active" | "Canceled" | "PastDue"
	isPaid: boolean;
	currentPeriodStart: string | null;
	currentPeriodEnd: string | null;
	cancelAtPeriodEnd: boolean;
	maxActiveQuotes: number;
	activeQuotesCount: number;
	remainingQuotes: number;
	canCreateQuote: boolean;
	usagePercentage: number;
}

export type SubscriptionDashboardState =
	| 'active'
	| 'available'
	| 'requires_paid'
	| 'enterprise_only'
	| 'deferred'
	| 'locked'
	| 'exhausted';

export const SubscriptionEntitlementCodes = {
	companyFree: 'COMPANY_FREE',
	companyPaid: 'COMPANY_PAID',
	companyProEnterprise: 'COMPANY_PRO_ENTERPRISE',
	talentFree: 'TALENT_FREE',
	talentPaid: 'TALENT_PAID',
	addOnCompanyAiSearchIntelligence: 'ADDON_COMPANY_AI_SEARCH_INTELLIGENCE',
	addOnCompanyContactChatPack: 'ADDON_COMPANY_CONTACT_CHAT_PACK',
	addOnCompanyOpenTalentWorkspace: 'ADDON_COMPANY_OPEN_TALENT_WORKSPACE',
	addOnCompanyTrustGovernance: 'ADDON_COMPANY_TRUST_GOVERNANCE',
	addOnCompanyApiMcpAccess: 'ADDON_COMPANY_API_MCP_ACCESS',
	addOnCompanyMarketIntelligence: 'ADDON_COMPANY_MARKET_INTELLIGENCE',
	addOnTalentOpportunityRadar: 'ADDON_TALENT_OPPORTUNITY_RADAR',
	addOnTalentAiProfilePositioning: 'ADDON_TALENT_AI_PROFILE_POSITIONING',
	addOnTalentCollaborationAllianceBoost: 'ADDON_TALENT_COLLABORATION_ALLIANCE_BOOST',
	addOnTalentCompanyIntelligence: 'ADDON_TALENT_COMPANY_INTELLIGENCE',
} as const;

export interface SubscriptionPackageSummary {
	plan: string;
	entitlementCode: string;
	labelKey: string;
	status: string;
	statusKey: string;
	isPaid: boolean;
	isEnterprise: boolean;
	currentPeriodStart: string | null;
	currentPeriodEnd: string | null;
	cancelAtPeriodEnd: boolean;
}

export interface SubscriptionRoleState {
	canManageSubscription: boolean;
	isReadOnly: boolean;
	reasonCode?: string | null;
}

export interface SubscriptionDashboardAction {
	code: string;
	labelKey: string;
	kind: string;
	isEnabled: boolean;
	disabledReasonCode?: string | null;
}

export interface SubscriptionUsageCard {
	code: string;
	labelKey: string;
	descriptionKey?: string | null;
	used?: number | null;
	limit?: number | null;
	remaining?: number | null;
	isUnlimited: boolean;
	state: SubscriptionDashboardState;
	requiredEntitlementCode?: string | null;
}

export interface SubscriptionAddOnCard {
	code: string;
	entitlementCode: string;
	labelKey: string;
	descriptionKey?: string | null;
	state: SubscriptionDashboardState;
	selfService: boolean;
	requiresEntitlementCode?: string | null;
	action: SubscriptionDashboardAction;
}

export interface SubscriptionDashboardWarning {
	code: string;
	severity: string;
	messageKey: string;
}

export interface SubscriptionDashboard {
	audience: 'company' | 'talent';
	package: SubscriptionPackageSummary;
	roleState: SubscriptionRoleState;
	primaryAction: SubscriptionDashboardAction;
	usageCards: SubscriptionUsageCard[];
	addOns: SubscriptionAddOnCard[];
	warnings: SubscriptionDashboardWarning[];
}

// ============================================================================
// Service
// ============================================================================

const subscriptionService = {
	/**
	 * Gets the Stripe Customer Portal URL for an organization.
	 * @param organizationId - The organization ID
	 * @param returnUrl - URL to redirect back to after portal session
	 * @returns The Stripe portal URL
	 */
	getOrganizationPortalUrl: async (
		organizationId: string,
		returnUrl?: string,
	): Promise<string> => {
		const params = new URLSearchParams();
		if (returnUrl) {
			params.append('returnUrl', returnUrl);
		}
		const queryString = params.toString();
		const url = `/api/organizations/${organizationId}/subscriptions/portal${queryString ? `?${queryString}` : ''}`;

		const response = await apiClient.get<PortalResponse>(url);
		return response.data.url;
	},

	/**
	 * Gets the Stripe Customer Portal URL for a talent.
	 * @param talentId - The talent ID
	 * @param returnUrl - URL to redirect back to after portal session
	 * @returns The Stripe portal URL
	 */
	getTalentPortalUrl: async (talentId: string, returnUrl?: string): Promise<string> => {
		const params = new URLSearchParams();
		if (returnUrl) {
			params.append('returnUrl', returnUrl);
		}
		const queryString = params.toString();
		const url = `/api/talentusers/me/subscriptions/portal${queryString ? `?${queryString}` : ''}`;

		const response = await apiClient.get<PortalResponse>(url);
		return response.data.url;
	},

	/**
	 * Gets current subscription details for an organization.
	 * @param organizationId - The organization ID
	 * @returns Current subscription data
	 */
	getOrganizationSubscription: async (organizationId: string): Promise<CurrentSubscription> => {
		const response = await apiClient.get<CurrentSubscription>(
			`/api/organizations/${organizationId}/subscriptions/current`,
		);
		return response.data;
	},

	/** @userstory US-CSUB-01, US-CSUB-02, US-CSUB-04, US-CSUB-06 */
	getOrganizationSubscriptionDashboard: async (
		organizationId: string,
	): Promise<SubscriptionDashboard> => {
		const response = await apiClient.get<SubscriptionDashboard>(
			`/api/organizations/${organizationId}/subscriptions/dashboard`,
		);
		return response.data;
	},

	/**
	 * Syncs the organization's subscription with Stripe.
	 * Creates a Free subscription if none exists.
	 * Should be called before accessing subscription data or portal.
	 * @param organizationId - The organization ID
	 * @returns Updated subscription data
	 */
	syncOrganizationSubscription: async (organizationId: string): Promise<CurrentSubscription> => {
		const response = await apiClient.post<CurrentSubscription>(
			`/api/organizations/${organizationId}/subscriptions/sync`,
		);
		return response.data;
	},

	/**
	 * Gets current subscription details for a talent.
	 * @param talentId - The talent ID
	 * @returns Current subscription data
	 */
	getTalentSubscription: async (_talentId: string): Promise<CurrentSubscription> => {
		void _talentId;
		const response = await apiClient.get<CurrentSubscription>(
			`/api/talentusers/me/subscriptions/current`,
		);
		return response.data;
	},

	/** @userstory US-TSUB-01, US-TSUB-02, US-TSUB-04, US-TSUB-05 */
	getTalentSubscriptionDashboard: async (): Promise<SubscriptionDashboard> => {
		const response = await apiClient.get<SubscriptionDashboard>(
			`/api/talentusers/me/subscriptions/dashboard`,
		);
		return response.data;
	},

	/**
	 * Syncs the talent's subscription with Stripe.
	 * Creates a Free subscription if none exists.
	 * Should be called before accessing subscription data or portal.
	 * @param talentId - The talent ID
	 * @returns Updated subscription data
	 */
	syncTalentSubscription: async (_talentId: string): Promise<CurrentSubscription> => {
		void _talentId;
		const response = await apiClient.post<CurrentSubscription>(
			`/api/talentusers/me/subscriptions/sync`,
		);
		return response.data;
	},

	/**
	 * Redirects user to Stripe Customer Portal for an organization.
	 * This performs a full page redirect.
	 * @param organizationId - The organization ID
	 * @param returnUrl - URL to redirect back to (defaults to current page)
	 */
	redirectToOrganizationPortal: async (
		organizationId: string,
		returnUrl?: string,
	): Promise<void> => {
		const url = await subscriptionService.getOrganizationPortalUrl(
			organizationId,
			returnUrl ?? window.location.href,
		);
		window.location.href = url;
	},

	/**
	 * Redirects talent to Stripe Customer Portal.
	 * @param talentId - The talent ID
	 * @param returnUrl - URL to redirect back to (defaults to current page)
	 */
	redirectToTalentPortal: async (talentId: string, returnUrl?: string): Promise<void> => {
		const url = await subscriptionService.getTalentPortalUrl(
			talentId,
			returnUrl ?? window.location.href,
		);
		window.location.href = url;
	},
};

export default subscriptionService;
